import { AlertTriangle, Calendar, Flag, User, MessageSquare } from "lucide-react";
import { formatDate, formatDateTime, isOverdue, PRIORITY_LABEL, STATUS_LABEL } from "@/lib/utils-task";
import { Button } from "@/components/ui/button";

const STATUS_STYLES = {
  Pending: "bg-amber-100 text-amber-800 border-amber-200",
  "In-Progress": "bg-indigo-100 text-indigo-800 border-indigo-200",
  Done: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

const PRIORITY_STYLES = {
  Low: "bg-slate-100 text-slate-700 border-slate-200",
  Medium: "bg-sky-100 text-sky-800 border-sky-200",
  High: "bg-rose-100 text-rose-800 border-rose-200",
};

export default function TaskCard({ task, onUpdateStatus, onEdit, onDelete, onViewRemarks, currentUser }) {
  const overdue = isOverdue(task);
  const canEdit =
    currentUser.role === "Admin" ||
    (currentUser.role === "Manager" && task.created_by_id === currentUser.id);
  const canUpdateStatus =
    currentUser.role === "Admin" || task.assignee_id === currentUser.id;

  const latestRemark = task.remarks?.length ? task.remarks[task.remarks.length - 1] : null;

  return (
    <div
      data-testid={`task-card-${task.id}`}
      className={`bg-white border rounded-md p-5 card-lift ${
        overdue
          ? "border-l-4 border-l-rose-600 border-slate-200 bg-rose-50/40"
          : "border-slate-200"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span
              className={`inline-flex items-center border text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${STATUS_STYLES[task.status]}`}
              data-testid={`task-status-${task.id}`}
            >
              {STATUS_LABEL[task.status] || task.status}
            </span>
            <span
              className={`inline-flex items-center gap-1 border text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${PRIORITY_STYLES[task.priority]}`}
            >
              <Flag className="h-3 w-3" />
              {PRIORITY_LABEL[task.priority] || task.priority}
            </span>
            {overdue && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-rose-600 text-white" data-testid={`task-overdue-${task.id}`}>
                <AlertTriangle className="h-3 w-3" />
                Overdue
              </span>
            )}
          </div>
          <h3 className="text-base sm:text-lg font-bold text-slate-900 leading-snug break-words">
            {task.title}
          </h3>
          {task.description && (
            <p className="mt-1.5 text-sm text-slate-600 leading-relaxed line-clamp-3">{task.description}</p>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-xs text-slate-600">
        <div className="flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5 text-slate-400" />
          <span className={overdue ? "text-rose-700 font-semibold" : ""}>{formatDate(task.due_date)}</span>
        </div>
        <div className="flex items-center gap-1.5 truncate">
          <User className="h-3.5 w-3.5 text-slate-400" />
          <span className="truncate">{task.assignee_name || "—"}</span>
        </div>
      </div>

      {latestRemark && (
        <div className="mt-4 pt-3 border-t border-slate-100">
          <div className="flex items-center gap-1.5 label-eyebrow">
            <MessageSquare className="h-3 w-3" /> Latest remark
          </div>
          <p className="mt-1.5 text-sm text-slate-700 leading-relaxed">{latestRemark.remark}</p>
          <p className="mt-1 text-[11px] text-slate-500">
            {latestRemark.user_name} · {formatDateTime(latestRemark.created_at)}
          </p>
          {task.remarks.length > 1 && (
            <button
              type="button"
              onClick={() => onViewRemarks?.(task)}
              className="mt-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 hover:underline transition-colors"
              data-testid={`task-view-remarks-${task.id}`}
            >
              + {task.remarks.length - 1} more · view all
            </button>
          )}
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {canUpdateStatus && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onUpdateStatus(task)}
            data-testid={`task-update-status-${task.id}`}
            className="transition-transform hover:-translate-y-[1px]"
          >
            Update status
          </Button>
        )}
        {canEdit && (
          <>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onEdit(task)}
              data-testid={`task-edit-${task.id}`}
            >
              Edit
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onDelete(task)}
              data-testid={`task-delete-${task.id}`}
              className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
            >
              Delete
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
